import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { ArrowRight, MapPin, Building2, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Case } from '../../../../shared/types/partnerships';

interface CaseCardProps {
  caseItem: Case;
  partnerSlug: string;
  canEdit?: boolean;
  onEdit?: (c: Case) => void;
}

export function CaseCard({ caseItem, partnerSlug, canEdit, onEdit }: CaseCardProps) {
  const { t } = useTranslation();

  return (
    <Card className="group flex flex-col hover:shadow-md transition-shadow">
      <div className="flex-1 p-5 space-y-3">
        <div className="flex flex-wrap gap-2 items-start">
          {canEdit && onEdit && (
            <button
              onClick={() => onEdit(caseItem)}
              className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-muted"
            >
              <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          )}
          {caseItem.market && (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
              <MapPin className="h-2.5 w-2.5" />
              {caseItem.market}
            </span>
          )}
          {caseItem.industry && (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
              <Building2 className="h-2.5 w-2.5" />
              {caseItem.industry}
            </span>
          )}
        </div>

        <div>
          <h3 className="font-semibold leading-tight">{caseItem.title}</h3>
          {caseItem.brand && (
            <p className="text-sm font-medium text-muted-foreground mt-0.5">{caseItem.brand}</p>
          )}
        </div>

        {caseItem.objective && (
          <p className="text-sm text-muted-foreground leading-snug line-clamp-2">{caseItem.objective}</p>
        )}

        {caseItem.highlights.length > 0 && (
          <ul className="space-y-1">
            {caseItem.highlights.slice(0, 2).map((h, i) => (
              <li key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                <span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-green-500 shrink-0" />
                {h}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="px-5 pb-4">
        <Link
          to={`/partnerships/${partnerSlug}/cases/${caseItem.caseId}`}
          className="inline-flex items-center gap-1 text-sm font-medium hover:gap-2 transition-all text-primary"
        >
          {t('partnerships.common.view_case')} <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </Card>
  );
}
